import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import RestoreIcon from '@mui/icons-material/Restore';
import { adminService, Ticker } from '../../../services/adminService';
import { tierLabel } from '../../../utils/formatters';

const TIER_DESCRIPTIONS: Record<number, string> = {
  1: 'Tier 1 — Full size ($0.05–$0.15/leg). NVDA, AVGO, MRVL, MU',
  2: 'Tier 2 — Half size ($0.20–$0.40/leg). ADBE, NOW, SNOW',
  3: 'Tier 3 — Skip earnings trades',
};

const TickerAdmin: React.FC = () => {
  const [tickers, setTickers] = useState<Ticker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 50;

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addInput, setAddInput] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');

  // Edit dialog
  const [editTarget, setEditTarget] = useState<Ticker | null>(null);
  const [editTier, setEditTier] = useState(1);
  const [editName, setEditName] = useState('');
  const [editSector, setEditSector] = useState('');
  const [editSaving, setEditSaving] = useState(false);


  const reload = (p = page) => {
    setLoading(true);
    adminService.listTickers(p, PAGE_SIZE)
      .then((data) => { setTickers(data.items); setTotal(data.total); setPage(data.page); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(1); }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError('');
    if (!addInput.trim()) { setAddError('Enter at least one ticker'); return; }
    setAdding(true);
    try {
      await adminService.addTickers(addInput.trim());
      setAddOpen(false);
      setAddInput('');
      setToast('Tickers added');
      reload();
    } catch (e: any) {
      setAddError(e.message);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (ticker: string) => {
    if (!window.confirm(`Deactivate ${ticker}?`)) return;
    try {
      await adminService.removeTicker(ticker);
      setToast(`${ticker} deactivated`);
      reload();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleReactivate = async (ticker: string) => {
    try {
      await adminService.updateTicker(ticker, { active: true });
      setToast(`${ticker} reactivated`);
      reload();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const openEdit = (t: Ticker) => {
    setEditTarget(t);
    setEditTier(t.liquidity_tier);
    setEditName(t.name ?? '');
    setEditSector(t.sector ?? '');
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      await adminService.updateTicker(editTarget.ticker, {
        liquidity_tier: editTier,
        name: editName || undefined,
        sector: editSector || undefined,
      });
      setEditTarget(null);
      setToast(`${editTarget.ticker} updated`);
      reload();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Ticker Watchlist</Typography>
          <Typography variant="body2" color="text.secondary">
            Active tickers the system collects option chain data for.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
          Add Tickers
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700 } }}>
              <TableCell>Ticker</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Sector</TableCell>
              <TableCell align="center">Liquidity Tier</TableCell>
              <TableCell>Added By</TableCell>
              <TableCell>Added</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tickers.map((t) => (
              <TableRow key={t.ticker}>
                <TableCell><Typography fontWeight={700}>{t.ticker}</Typography></TableCell>
                <TableCell>{t.name ?? <Typography color="text.secondary" variant="body2">—</Typography>}</TableCell>
                <TableCell>{t.sector ?? <Typography color="text.secondary" variant="body2">—</Typography>}</TableCell>
                <TableCell align="center">
                  <Tooltip title={TIER_DESCRIPTIONS[t.liquidity_tier] ?? ''}>
                    <Chip label={tierLabel(t.liquidity_tier)} size="small" />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {t.added_by_name || t.added_by_email ? (
                    <Tooltip title={t.added_by_email || ''}>
                      <Typography variant="body2">{t.added_by_name || t.added_by_email}</Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {t.added_at ? (
                    <Typography variant="body2" sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                      {new Date(t.added_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </Typography>
                  ) : '—'}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={t.active ? 'Active' : 'Inactive'}
                    color={t.active ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(t)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {t.active ? (
                    <Tooltip title="Deactivate">
                      <IconButton size="small" color="warning" onClick={() => handleRemove(t.ticker)}>
                        <PauseCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Reactivate">
                      <IconButton size="small" color="success" onClick={() => handleReactivate(t.ticker)}>
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!tickers.length && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" py={2}>No tickers yet. Add some to get started.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
          <Button size="small" disabled={page <= 1} onClick={() => reload(page - 1)}>Previous</Button>
          <Typography variant="body2">Page {page} of {totalPages} ({total} tickers)</Typography>
          <Button size="small" disabled={page >= totalPages} onClick={() => reload(page + 1)}>Next</Button>
        </Box>
      )}

      {/* Add dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Tickers to Watchlist</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Enter one or more ticker symbols separated by commas.
          </Typography>
          <Box component="form" id="add-form" onSubmit={handleAdd}>
            <TextField
              label="Tickers"
              placeholder="NVDA, AVGO, MRVL, MU"
              value={addInput}
              onChange={(e) => setAddInput(e.target.value)}
              fullWidth
              size="small"
              autoFocus
              helperText="Symbols will be uppercased automatically"
            />
          </Box>
          {addError && <Alert severity="error" sx={{ mt: 1 }}>{addError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={adding}>Cancel</Button>
          <Button type="submit" form="add-form" variant="contained" disabled={adding}>
            {adding ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit {editTarget?.ticker}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            size="small"
            fullWidth
            placeholder="e.g. NVIDIA Corporation"
          />
          <TextField
            label="Sector"
            value={editSector}
            onChange={(e) => setEditSector(e.target.value)}
            size="small"
            fullWidth
            placeholder="e.g. Technology"
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Liquidity Tier</InputLabel>
            <Select
              value={editTier}
              label="Liquidity Tier"
              onChange={(e) => setEditTier(Number(e.target.value))}
            >
              {[1, 2, 3].map((tier) => (
                <MenuItem key={tier} value={tier}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{tierLabel(tier)}</Typography>
                    <Typography variant="caption" color="text.secondary">{TIER_DESCRIPTIONS[tier]}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)} disabled={editSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving}>
            {editSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast('')}
        message={toast}
      />
    </Box>
  );
};

export default TickerAdmin;
